import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';

import { NOTIFICATION_DEFAULT_ERROR_MESSAGE, useNotificationContext } from 'application/providers/NotificationProvider';

import {
  type MediaPromoClipsQueryVariables,
  PromoSelectionDraftAction,
  useMediaPromoSelectionDraftMutation,
} from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';

import { getPromoError } from './utils';
import useOptimisticPromoSelection from './useOptimisticPromoSelection';

export const usePromoSelectionActions = (
  companyId: string | undefined,
  draftId: string | undefined,
  filters: MediaPromoClipsQueryVariables['filters'] | undefined,
  isInitialLoading: boolean,
  selectedCount: number,
  refreshCurrentFirstPage: () => void,
  applyPromoSelection: (promoId: string, isSelected: boolean) => void,
  clearPromoSelectionOverrides: () => void,
  setSelectedCount: Dispatch<SetStateAction<number>>
) => {
  const notification = useNotificationContext();
  const isMountedRef = useRef(true);
  const currentDraftIdRef = useRef(draftId);
  const [isBulkSelectionActionPending, setIsBulkSelectionActionPending] = useState(false);
  const [updateDraft] = useMediaPromoSelectionDraftMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    currentDraftIdRef.current = draftId;
  }, [draftId]);
  const { hasPendingPromoSelections, onPromoSelectionChange } = useOptimisticPromoSelection({
    companyId,
    draftId,
    isBulkSelectionActionPending,
    isInitialLoading,
    applyPromoSelection,
    setSelectedCount,
  });

  const onSelectAll = useCallback(() => {
    if (!companyId || !draftId || isInitialLoading || isBulkSelectionActionPending || hasPendingPromoSelections) {
      return;
    }

    setIsBulkSelectionActionPending(true);

    updateDraft({
      variables: {
        input: {
          action: PromoSelectionDraftAction.SelectAll,
          companyId,
          filters,
          promoSelectionDraftId: draftId,
        },
      },
    })
      .then((result) => {
        const draftResult = result.data?.result;

        if (!draftResult?.ok) {
          throw getPromoError(draftResult?.errors?.[0]?.messages[0]);
        }

        if (isMountedRef.current && currentDraftIdRef.current === draftId) {
          clearPromoSelectionOverrides();
          setSelectedCount(draftResult.selectedCount);
          refreshCurrentFirstPage();
        }
      })
      .catch((error) => {
        if (isMountedRef.current && currentDraftIdRef.current === draftId) {
          notification.showErrorNotification({
            children: error instanceof Error ? error.message : NOTIFICATION_DEFAULT_ERROR_MESSAGE,
          });
        }
      })
      .finally(() => {
        if (isMountedRef.current && currentDraftIdRef.current === draftId) {
          setIsBulkSelectionActionPending(false);
        }
      });
  }, [
    companyId,
    clearPromoSelectionOverrides,
    draftId,
    filters,
    isInitialLoading,
    hasPendingPromoSelections,
    isBulkSelectionActionPending,
    notification,
    refreshCurrentFirstPage,
    setSelectedCount,
    updateDraft,
  ]);

  const onReset = useCallback(() => {
    if (
      !companyId ||
      !draftId ||
      isInitialLoading ||
      isBulkSelectionActionPending ||
      hasPendingPromoSelections ||
      selectedCount === 0
    ) {
      return;
    }

    setIsBulkSelectionActionPending(true);

    updateDraft({
      variables: {
        input: {
          action: PromoSelectionDraftAction.DeselectAll,
          companyId,
          promoSelectionDraftId: draftId,
        },
      },
    })
      .then((result) => {
        const draftResult = result.data?.result;

        if (!draftResult?.ok) {
          throw getPromoError(draftResult?.errors?.[0]?.messages[0]);
        }

        if (isMountedRef.current && currentDraftIdRef.current === draftId) {
          clearPromoSelectionOverrides();
          setSelectedCount(draftResult.selectedCount);
          refreshCurrentFirstPage();
        }
      })
      .catch((error) => {
        if (isMountedRef.current && currentDraftIdRef.current === draftId) {
          notification.showErrorNotification({
            children: error instanceof Error ? error.message : NOTIFICATION_DEFAULT_ERROR_MESSAGE,
          });
        }
      })
      .finally(() => {
        if (isMountedRef.current && currentDraftIdRef.current === draftId) {
          setIsBulkSelectionActionPending(false);
        }
      });
  }, [
    companyId,
    clearPromoSelectionOverrides,
    draftId,
    isInitialLoading,
    hasPendingPromoSelections,
    isBulkSelectionActionPending,
    notification,
    refreshCurrentFirstPage,
    selectedCount,
    setSelectedCount,
    updateDraft,
  ]);

  return {
    isBulkSelectionActionPending,
    isSelectionActionPending: isBulkSelectionActionPending || hasPendingPromoSelections,
    onPromoSelectionChange,
    onReset,
    onSelectAll,
  };
};
